import React, { useState, useEffect } from "react";
import AdminLayout from "./layout/AdminLayout";
import API_BASE, { UPLOADS_BASE } from "../api";
import ImageUploadDropzone from "./components/ImageUploadDropzone";
import "./productmanager.css";

function RiceManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    net_weight: "",
    image: null
  });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const loadProducts = async () => {
    try {
      const res = await fetch(`${API_BASE}/rice_manager/get_rice_products.php`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setProducts(data);
      } else if (data && Array.isArray(data.products)) {
        setProducts(data.products);
      }
    } catch (err) {
      console.error("Error loading rice products:", err);
      showToast("error", "Failed to load rice products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      net_weight: "",
      image: null
    });
  };

  const addProduct = async (e) => {
    if (e) e.preventDefault();

    if (!form.title.trim()) {
      showToast("error", "Rice title is required.");
      return;
    }

    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("net_weight", form.net_weight);

    if (form.image) {
      fd.append("image", form.image);
    }

    try {
      const res = await fetch(`${API_BASE}/rice_manager/add_rice_product.php`, {
        method: "POST",
        body: fd
      });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to add rice product");
      }

      showToast("success", "Rice product added successfully!");
      setShowAddModal(false);
      resetForm();
      loadProducts();
    } catch (err) {
      console.error("Add rice error:", err);
      showToast("error", err.message || "Failed to add rice");
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (p) => {
    setSelectedProduct(p);
    setForm({
      title: p.title || "",
      description: p.description || "",
      net_weight: p.net_weight || "",
      image: null
    });
    setShowEditModal(true);
  };

  const updateProduct = async (e) => {
    if (e) e.preventDefault();

    if (!form.title.trim()) {
      showToast("error", "Rice title is required.");
      return;
    }

    setSubmitting(true);
    const fd = new FormData();
    fd.append("id", selectedProduct.id);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("net_weight", form.net_weight);

    if (form.image) {
      fd.append("image", form.image);
    }

    try {
      const res = await fetch(`${API_BASE}/rice_manager/update_rice_product.php`, {
        method: "POST",
        body: fd
      });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to update rice product");
      }

      showToast("success", "Rice product updated successfully!");
      setShowEditModal(false);
      resetForm();
      loadProducts();
    } catch (err) {
      console.error("Update error:", err);
      showToast("error", err.message || "Failed to update rice");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteProduct = async () => {
    if (!selectedProduct) return;
    setSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("id", selectedProduct.id);

      const res = await fetch(`${API_BASE}/rice_manager/delete_rice_product.php`, {
        method: "POST",
        body: fd
      });
      const data = await res.json();

      if (data && data.success === false) {
        throw new Error(data.message || "Delete failed");
      }

      showToast("success", "Product deleted successfully");
      setShowDeleteModal(false);
      loadProducts();
    } catch (err) {
      console.error("Delete error:", err);
      showToast("error", err.message || "Failed to delete rice");
    } finally {
      setSubmitting(false);
    }
  };

  const getProductImgUrl = (imageName) => {
    if (!imageName) return null;
    return `${UPLOADS_BASE}/rice_products/${imageName}`;
  };

  return (
    <AdminLayout>
      <div className="pm-container">
        {/* Header */}
        <div className="pm-header">
          <div className="pm-header-info">
            <h2>Traditional Rice Management</h2>
            <p>Manage Ceylon heirloom and traditional organic rice products</p>
          </div>
          <button
            className="pm-add-btn"
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
          >
            <i className="fas fa-plus"></i> Add New Rice
          </button>
        </div>

        {/* Toast Notification */}
        {toast && (
          <div className={`pm-toast ${toast.type === "success" ? "pm-toast-success" : "pm-toast-error"}`}>
            <i className={`fas ${toast.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
            <span>{toast.message}</span>
          </div>
        )}

        {/* Product Grid */}
        {loading ? (
          <div className="loader"></div>
        ) : products.length === 0 ? (
          <div className="pm-empty-state">
            <i className="fas fa-seedling"></i>
            <h3>No rice products found</h3>
            <p>Click "Add New Rice" to create your first item.</p>
          </div>
        ) : (
          <div className="pm-grid">
            {products.map((p) => {
              const imgUrl = getProductImgUrl(p.image);
              return (
                <div className="pm-card" key={p.id}>
                  <div className="pm-card-img-wrapper">
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt={p.title}
                        className="pm-img"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="pm-img-placeholder"
                      style={{ display: imgUrl ? 'none' : 'flex' }}
                    >
                      <i className="fas fa-seedling"></i>
                      <span>No Image</span>
                    </div>
                    {p.net_weight && <span className="pm-weight-tag">{p.net_weight}</span>}
                  </div>

                  <div className="pm-card-body">
                    <h3 className="pm-name">{p.title}</h3>
                    <p className="pm-sub">{p.description || "Ceylon Traditional Rice"}</p>

                    <div className="pm-actions">
                      <button
                        className="pm-edit"
                        onClick={() => openEdit(p)}
                        title="Edit Rice"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="pm-delete"
                        onClick={() => {
                          setSelectedProduct(p);
                          setShowDeleteModal(true);
                        }}
                        title="Delete Rice"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ADD MODAL */}
        {showAddModal && (
          <div className="pm-modal-overlay" onClick={() => !submitting && setShowAddModal(false)}>
            <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pm-modal-header">
                <h2>Add New Rice Product</h2>
                <button
                  className="pm-modal-close"
                  onClick={() => setShowAddModal(false)}
                  disabled={submitting}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={addProduct}>
                <div className="pm-modal-body">
                  <div className="pm-form-horizontal">
                    <div className="pm-left">
                      <div className="pm-form-group">
                        <label className="pm-label">Rice Title *</label>
                        <input
                          type="text"
                          name="title"
                          placeholder="e.g. Suwandel Organic Traditional Rice"
                          className="pm-input"
                          value={form.title}
                          onChange={handleFormChange}
                          required
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Net Weight</label>
                        <input
                          type="text"
                          name="net_weight"
                          placeholder="e.g. 1kg / 5kg / 10kg"
                          className="pm-input"
                          value={form.net_weight}
                          onChange={handleFormChange}
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Description</label>
                        <textarea
                          name="description"
                          placeholder="Health benefits, cooking instructions, origin..."
                          className="pm-textarea"
                          value={form.description}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>

                    <div className="pm-right">
                      <ImageUploadDropzone
                        value={form.image}
                        label="Product Image (Auto-WebP)"
                        onChange={(file) => setForm({ ...form, image: file })}
                        onClear={() => setForm({ ...form, image: null })}
                      />
                    </div>
                  </div>
                </div>

                <div className="pm-modal-footer">
                  <button
                    type="button"
                    className="pm-cancel"
                    onClick={() => setShowAddModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="pm-save" disabled={submitting}>
                    {submitting && <span className="pm-btn-spinner" />}
                    <span>{submitting ? "Saving & Optimizing..." : "Save Rice"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT MODAL */}
        {showEditModal && selectedProduct && (
          <div className="pm-modal-overlay" onClick={() => !submitting && setShowEditModal(false)}>
            <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pm-modal-header">
                <h2>Edit Rice Product</h2>
                <button
                  className="pm-modal-close"
                  onClick={() => setShowEditModal(false)}
                  disabled={submitting}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={updateProduct}>
                <div className="pm-modal-body">
                  <div className="pm-form-horizontal">
                    <div className="pm-left">
                      <div className="pm-form-group">
                        <label className="pm-label">Rice Title *</label>
                        <input
                          type="text"
                          name="title"
                          className="pm-input"
                          value={form.title}
                          onChange={handleFormChange}
                          required
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Net Weight</label>
                        <input
                          type="text"
                          name="net_weight"
                          className="pm-input"
                          value={form.net_weight}
                          onChange={handleFormChange}
                        />
                      </div>

                      <div className="pm-form-group">
                        <label className="pm-label">Description</label>
                        <textarea
                          name="description"
                          className="pm-textarea"
                          value={form.description}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>

                    <div className="pm-right">
                      <ImageUploadDropzone
                        value={form.image}
                        existingUrl={getProductImgUrl(selectedProduct.image)}
                        label="Change Image (Auto-WebP)"
                        onChange={(file) => setForm({ ...form, image: file })}
                        onClear={() => setForm({ ...form, image: null })}
                      />
                    </div>
                  </div>
                </div>

                <div className="pm-modal-footer">
                  <button
                    type="button"
                    className="pm-cancel"
                    onClick={() => setShowEditModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="pm-save" disabled={submitting}>
                    {submitting && <span className="pm-btn-spinner" />}
                    <span>{submitting ? "Updating..." : "Update Rice"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {showDeleteModal && selectedProduct && (
          <div className="pm-modal-overlay" onClick={() => !submitting && setShowDeleteModal(false)}>
            <div className="pm-modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="pm-delete-icon">
                <i className="fas fa-trash-alt"></i>
              </div>
              <h3>Delete Rice?</h3>
              <p>
                Are you sure you want to delete <strong>"{selectedProduct.title}"</strong>?
              </p>
              <div className="pm-delete-actions">
                <button
                  className="pm-cancel"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="pm-delete-confirm"
                  onClick={deleteProduct}
                  disabled={submitting}
                >
                  {submitting ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default RiceManager;
